import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useEvents from "../../api/events";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import { Checkbox } from "../../components/ui/checkbox";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  GripVertical,
  Mail,
  Type,
  List,
  CheckSquare,
  Hash,
  AlignLeft,
  Calendar as CalendarIcon,
  MapPin,
  Bell,
  Eye,
  Image as ImageIcon,
  Copy,
  Lock,
  Info,
  Check,
  Ticket,
  GraduationCap,
  CreditCard,
  Euro,
  Clock,
} from "lucide-react";
import { Switch } from "../../components/ui/switch";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { Reorder, useDragControls } from "framer-motion";

const timeOptions = [];
for (let i = 0; i < 24; i++) {
  for (let j = 0; j < 60; j += 15) {
    const hour = i.toString().padStart(2, "0");
    const minute = j.toString().padStart(2, "0");
    timeOptions.push(`${hour}:${minute}`);
  }
}

// 1 = maandag .. 7 = zondag (ISO). Matches the consumer-side formatter.
const WEEKDAYS = [
  { n: 1, short: "Ma", label: "Maandag" },
  { n: 2, short: "Di", label: "Dinsdag" },
  { n: 3, short: "Wo", label: "Woensdag" },
  { n: 4, short: "Do", label: "Donderdag" },
  { n: 5, short: "Vr", label: "Vrijdag" },
  { n: 6, short: "Za", label: "Zaterdag" },
  { n: 7, short: "Zo", label: "Zondag" },
];

const SCHEDULE_TYPES = [
  { value: "single", label: "Eenmalig", desc: "Eén datum en tijdstip." },
  { value: "range", label: "Periode", desc: "Van een start- tot einddatum." },
  {
    value: "weekly",
    label: "Wekelijks",
    desc: "Terugkerend op vaste weekdagen.",
  },
];

// Groepering voor de website. De waarde wordt 1-op-1 door de consumer-site
// gebruikt om de activiteit onder de juiste rubriek te tonen.
const ENROLLMENT_CATEGORIES = [
  { value: "", label: "— Automatisch (op naam) —" },
  { value: "jeugd", label: "Lessen Jeugd" },
  { value: "start-to-tennis", label: "Start to Tennis" },
  { value: "volwassenen", label: "Lessen Volwassenen" },
  { value: "stages", label: "Stages" },
];

// Lesvorm van een lessenreeks. Leeg = de website vermeldt er niets over.
const LESVORMEN = [
  { value: "", label: "— Niet vermelden —" },
  { value: "vast", label: "Vaste lessen (elke week hetzelfde moment)" },
  { value: "variabel", label: "Variabele lessen (per les af te spreken)" },
  { value: "keuze", label: "Deelnemer kiest zelf (vast of variabel)" },
];

/**
 * Formules = prijsvarianten binnen één inschrijving ("Kids Tennis · 1 uur"
 * € 325, "· 2 uur" € 575). De backend levert een array; oudere rijen kunnen
 * nog een JSON-string zijn. Bedragen houden we als tekst bij zolang er in het
 * formulier getypt wordt.
 */
const parseFormules = (raw) => {
  let list = raw;
  if (typeof list === "string") {
    try {
      list = JSON.parse(list);
    } catch {
      list = null;
    }
  }
  if (!Array.isArray(list)) return [];
  return list.map((f, i) => ({
    id: String(f?.id != null && f.id !== "" ? f.id : i),
    label: String(f?.label ?? ""),
    niveau_id: f?.niveau_id != null ? String(f.niveau_id) : "",
    // Lesmomenten: per dag een eigen uur, want dinsdag hoeft niet hetzelfde
    // moment te zijn als zaterdag.
    momenten: Array.isArray(f?.momenten)
      ? f.momenten.map((m, j) => ({
          id: String(m?.id != null && m.id !== "" ? m.id : `${i}-${j}`),
          dag: m?.dag != null ? String(m.dag) : "",
          time: String(m?.time ?? ""),
          timeEnd: String(m?.timeEnd ?? ""),
        }))
      : [],
    price_member: f?.price_member != null ? String(f.price_member) : "",
    price_non_member:
      f?.price_non_member != null ? String(f.price_non_member) : "",
  }));
};

const WEEKDAGEN = [
  { value: "1", label: "Maandag" },
  { value: "2", label: "Dinsdag" },
  { value: "3", label: "Woensdag" },
  { value: "4", label: "Donderdag" },
  { value: "5", label: "Vrijdag" },
  { value: "6", label: "Zaterdag" },
  { value: "7", label: "Zondag" },
];

/**
 * Niveaus = de groepen waaruit de deelnemer kiest in het inschrijfformulier.
 * Een lijst met losse namen aanvaarden we ook, zodat oudere data blijft werken.
 */
const parseNiveaus = (raw) => {
  let list = raw;
  if (typeof list === "string") {
    try {
      list = JSON.parse(list);
    } catch {
      list = null;
    }
  }
  if (!Array.isArray(list)) return [];
  return list.map((n, i) =>
    typeof n === "string"
      ? { id: String(i), label: n }
      : {
          id: String(n?.id != null && n.id !== "" ? n.id : i),
          label: String(n?.label ?? ""),
        },
  );
};

const EventEditor = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { getEventById, createEvent, updateEvent } = useEvents();
  const { theme } = useTheme();
  const { company } = useAuth();
  const isDark = theme === "dark";
  const isNew = eventId === "new" || !eventId;

  // Shareable link built from the company's own domain (company gegevens).
  const shareUrl = (() => {
    let base = (company?.domeinnaam || "").trim().replace(/\/+$/, "");
    if (!base) return "";
    if (!/^https?:\/\//i.test(base)) base = `https://${base}`;
    return `${base}/event/${eventId}`;
  })();

  const [loading, setLoading] = useState(!isNew);
  const [activeTab, setActiveTab] = useState("general");
  const [formData, setFormData] = useState({
    name: "",
    date: "",
    location: "",
    details: "",
    max_capacity: "",
    notification_email: "",
    is_private: true,
    type: "event",
    payment_required: false,
    payment_choice_allowed: false,
    price_member: "",
    price_non_member: "",
    formules: [],
    niveaus: [],
    currency: "EUR",
    schedule_config: {
      type: "single",
      weekdays: [],
      startDate: "",
      endDate: "",
      time: "",
      timeEnd: "",
    },
    registration_deadline: "",
    registration_closed: false,
    category: "",
    lesvorm: "",
    aantal_lessen: "",
    periode_info: "",
    groepsindeling: "",
    beschikbare_dagen: "",
    reminder_enabled: false,
    reminder_days_before: 3,
    form_config: { fields: [] },
    email_config: { subject: "", body: "" },
  });

  const [copied, setCopied] = useState(false);

  // Dynamische benaming: evenement vs inschrijving.
  const isEnrollment = formData.type === "enrollment";
  const typeNoun = isEnrollment ? "inschrijving" : "evenement";
  const typeNounCap = isEnrollment ? "Inschrijving" : "Evenement";

  const generateUid = () => Math.random().toString(36).substr(2, 9);

  useEffect(() => {
    if (!isNew && eventId) {
      loadEvent();
    } else {
      // Default form config
      setFormData((prev) => ({
        ...prev,
        form_config: {
          fields: [
            {
              id: "zone",
              label: "Zone",
              type: "text",
              required: true,
              _uid: generateUid(),
            },
            {
              id: "firstname",
              label: "Voornaam",
              type: "text",
              required: true,
              _uid: generateUid(),
            },
            {
              id: "lastname",
              label: "Naam",
              type: "text",
              required: true,
              _uid: generateUid(),
            },
            {
              id: "function",
              label: "Functie",
              type: "text",
              required: true,
              _uid: generateUid(),
            },
            {
              id: "email",
              label: "Mailadres",
              type: "email",
              required: true,
              _uid: generateUid(),
            },
          ],
        },
      }));
    }
  }, [eventId, isNew]);

  const loadEvent = async () => {
    try {
      const data = await getEventById(eventId);
      const formConfig =
        typeof data.form_config === "string"
          ? JSON.parse(data.form_config)
          : data.form_config || { fields: [] };

      // Ensure all fields have a stable UID for React keys
      if (formConfig.fields) {
        formConfig.fields = formConfig.fields.map((f) => ({
          ...f,
          _uid: f._uid || generateUid(),
        }));
      }

      const emailConfig =
        typeof data.email_config === "string"
          ? JSON.parse(data.email_config)
          : data.email_config || { subject: "", body: "" };

      let scheduleConfig = data.schedule_config;
      if (typeof scheduleConfig === "string") {
        try {
          scheduleConfig = JSON.parse(scheduleConfig);
        } catch {
          scheduleConfig = null;
        }
      }
      scheduleConfig = {
        type: "single",
        weekdays: [],
        startDate: "",
        endDate: "",
        time: "",
        timeEnd: "",
        ...(scheduleConfig || {}),
      };

      let dateStr = "";
      if (data.date) {
        const d = new Date(data.date);
        // Use UTC getters to avoid timezone conversion
        // The database stores the time as-is (not as UTC), but server sends it with Z suffix
        // So we need to read UTC values to get the original intended time
        const year = d.getUTCFullYear();
        const month = String(d.getUTCMonth() + 1).padStart(2, "0");
        const day = String(d.getUTCDate()).padStart(2, "0");
        const hours = String(d.getUTCHours()).padStart(2, "0");
        const minutes = String(d.getUTCMinutes()).padStart(2, "0");
        dateStr = `${year}-${month}-${day}T${hours}:${minutes}`;
      }

      let deadlineStr = "";
      if (data.registration_deadline) {
        const d = new Date(data.registration_deadline);
        if (!Number.isNaN(d.getTime())) {
          const y = d.getUTCFullYear();
          const mo = String(d.getUTCMonth() + 1).padStart(2, "0");
          const da = String(d.getUTCDate()).padStart(2, "0");
          const hh = String(d.getUTCHours()).padStart(2, "0");
          const mi = String(d.getUTCMinutes()).padStart(2, "0");
          deadlineStr = `${y}-${mo}-${da}T${hh}:${mi}`;
        }
      }

      setFormData({
        ...data,
        is_private: !!data.is_private,
        payment_required: !!data.payment_required,
        payment_choice_allowed: !!data.payment_choice_allowed,
        type: data.type || "event",
        currency: data.currency || "EUR",
        price_member: data.price_member != null ? String(data.price_member) : "",
        price_non_member:
          data.price_non_member != null ? String(data.price_non_member) : "",
        formules: parseFormules(data.formules),
        niveaus: parseNiveaus(data.niveaus),
        date: dateStr,
        schedule_config: scheduleConfig,
        registration_deadline: deadlineStr,
        registration_closed: !!data.registration_closed,
        category: data.category || "",
        lesvorm: data.lesvorm || "",
        aantal_lessen:
          data.aantal_lessen != null ? String(data.aantal_lessen) : "",
        periode_info: data.periode_info || "",
        groepsindeling: data.groepsindeling || "",
        beschikbare_dagen: data.beschikbare_dagen || "",
        reminder_enabled: !!data.reminder_enabled,
        reminder_days_before:
          data.reminder_days_before != null ? data.reminder_days_before : 3,
        form_config: formConfig,
        email_config: emailConfig,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const addFormule = () => {
    setFormData((prev) => ({
      ...prev,
      formules: [
        ...(prev.formules || []),
        {
          id: generateUid(),
          label: "",
          niveau_id: "",
          momenten: [],
          price_member: "",
          price_non_member: "",
        },
      ],
    }));
  };

  const addMoment = (formuleId) => {
    updateFormule(formuleId, undefined, (f) => ({
      momenten: [
        ...(f.momenten || []),
        { id: generateUid(), dag: "", time: "", timeEnd: "" },
      ],
    }));
  };

  const updateMoment = (formuleId, momentId, patch) => {
    updateFormule(formuleId, undefined, (f) => ({
      momenten: (f.momenten || []).map((m) =>
        m.id === momentId ? { ...m, ...patch } : m,
      ),
    }));
  };

  const removeMoment = (formuleId, momentId) => {
    updateFormule(formuleId, undefined, (f) => ({
      momenten: (f.momenten || []).filter((m) => m.id !== momentId),
    }));
  };

  const addNiveau = () => {
    setFormData((prev) => ({
      ...prev,
      niveaus: [...(prev.niveaus || []), { id: generateUid(), label: "" }],
    }));
  };

  const updateNiveau = (id, label) => {
    setFormData((prev) => ({
      ...prev,
      niveaus: (prev.niveaus || []).map((n) =>
        n.id === id ? { ...n, label } : n,
      ),
    }));
  };

  // Een niveau verdwijnt samen met de koppeling: formules die eraan hingen
  // gelden daarna voor elk niveau, in plaats van naar een dood id te wijzen.
  const removeNiveau = (id) => {
    setFormData((prev) => ({
      ...prev,
      niveaus: (prev.niveaus || []).filter((n) => n.id !== id),
      formules: (prev.formules || []).map((f) =>
        f.niveau_id === id ? { ...f, niveau_id: "" } : f,
      ),
    }));
  };

  // `patch` voor een simpele wijziging, of `derive` wanneer de nieuwe waarde van
  // de formule zelf afhangt (bv. een lesmoment toevoegen aan haar lijst).
  const updateFormule = (id, patch, derive) => {
    setFormData((prev) => ({
      ...prev,
      formules: (prev.formules || []).map((f) =>
        f.id === id ? { ...f, ...(derive ? derive(f) : patch) } : f,
      ),
    }));
  };

  const removeFormule = (id) => {
    setFormData((prev) => ({
      ...prev,
      formules: (prev.formules || []).filter((f) => f.id !== id),
    }));
  };

  const setSchedule = (patch) => {
    setFormData((prev) => ({
      ...prev,
      schedule_config: { ...prev.schedule_config, ...patch },
    }));
  };

  const toggleWeekday = (n) => {
    setFormData((prev) => {
      const current = prev.schedule_config?.weekdays || [];
      const weekdays = current.includes(n)
        ? current.filter((d) => d !== n)
        : [...current, n].sort((a, b) => a - b);
      return {
        ...prev,
        schedule_config: { ...prev.schedule_config, weekdays },
      };
    });
  };

  // Eén Van/Tot-paar. Wordt zowel voor het vaste uur van de activiteit als per
  // niveau gebruikt, zodat beide er hetzelfde uitzien.
  const renderVanTot = (time, timeEnd, onChange) => (
    <div className="flex items-end gap-3">
      <div className="space-y-1">
        <label
          className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
        >
          Van
        </label>
        <select
          value={time || ""}
          onChange={(e) => onChange({ time: e.target.value })}
          className={`w-28 h-9 px-2 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white focus:ring-white/20" : "bg-gray-50 border-gray-200 text-gray-900 focus:ring-gray-300"}`}
        >
          <option value="">--:--</option>
          {timeOptions.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-1">
        <label
          className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
        >
          Tot
        </label>
        <select
          value={timeEnd || ""}
          onChange={(e) => onChange({ timeEnd: e.target.value })}
          className={`w-28 h-9 px-2 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white focus:ring-white/20" : "bg-gray-50 border-gray-200 text-gray-900 focus:ring-gray-300"}`}
        >
          <option value="">--:--</option>
          {timeOptions.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const perFormule = formData.schedule_config?.timeMode === "formule";

  /**
   * Het uur van de reeks. Eén Van/Tot volstaat zolang alle groepen op hetzelfde
   * moment spelen. Duurt Kids Tennis een uur en Competitie 2,5 uur — of speelt
   * dinsdag op een ander uur dan zaterdag — dan zet je de lesmomenten per
   * formule, in het blok hieronder.
   */
  const renderTimeRange = () => (
    <div className="space-y-3">
      {isEnrollment && (
        <div className="flex flex-wrap gap-2">
          {[
            { value: "vast", label: "Zelfde dagen & uren voor iedereen" },
            { value: "formule", label: "Dagen & uren per formule" },
          ].map((opt) => {
            const active =
              (formData.schedule_config?.timeMode || "vast") === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSchedule({ timeMode: opt.value })}
                className={`h-8 px-3 rounded-lg text-[12px] font-medium transition-colors ${
                  active
                    ? isDark
                      ? "bg-white text-black"
                      : "bg-gray-900 text-white"
                    : isDark
                      ? "bg-white/[0.06] text-gray-300 hover:bg-white/[0.1]"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}

      {!perFormule &&
        renderVanTot(
          formData.schedule_config?.time,
          formData.schedule_config?.timeEnd,
          setSchedule,
        )}

      {perFormule && (
        <p
          className={`text-[11px] flex items-center gap-1.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
        >
          <Info className="h-3 w-3 flex-shrink-0" />
          Elke formule krijgt hieronder haar eigen lesmomenten: per moment een
          dag met een eigen uur. Zo kan Kids Tennis op dinsdag en zaterdag
          vallen en Initiatie op woensdag en zondag, elk met hun eigen uren.
        </p>
      )}
    </div>
  );

  const scheduleSummary = () => {
    const sc = formData.schedule_config || {};
    const fmt = (d) => {
      if (!d) return null;
      const [y, m, day] = d.split("-");
      return day && m ? `${day}/${m}/${y}` : null;
    };
    if (sc.type === "range") {
      const s = fmt(sc.startDate);
      const e = fmt(sc.endDate);
      if (s && e) return `Loopt van ${s} t.e.m. ${e}.`;
      return "Kies een start- en einddatum.";
    }
    if (sc.type === "weekly") {
      const names = (sc.weekdays || [])
        .map((n) => WEEKDAYS.find((w) => w.n === n)?.label)
        .filter(Boolean);
      if (names.length === 0) return "Kies minstens één weekdag.";
      const joined =
        names.length === 1
          ? names[0]
          : names.slice(0, -1).join(", ") + " & " + names[names.length - 1];
      return `Elke ${joined.toLowerCase()}.`;
    }
    return "Eén datum en tijdstip.";
  };

  // Default form for a paid enrollment: participant + member status + contact.
  const enrollmentFormConfig = () => ({
    fields: [
      {
        id: "voornaam",
        label: "Voornaam deelnemer",
        type: "text",
        required: true,
        _uid: generateUid(),
      },
      {
        id: "naam",
        label: "Naam deelnemer",
        type: "text",
        required: true,
        _uid: generateUid(),
      },
      {
        id: "geboortedatum",
        label: "Geboortedatum",
        type: "text",
        required: true,
        _uid: generateUid(),
      },
      {
        id: "email",
        label: "Mailadres",
        type: "email",
        required: true,
        _uid: generateUid(),
      },
      {
        id: "telefoon",
        label: "Telefoon",
        type: "text",
        required: true,
        _uid: generateUid(),
      },
    ],
  });

  const handleTypeChange = (newType) => {
    setFormData((prev) => {
      const next = { ...prev, type: newType };
      // Only for a brand-new activity: switching to "enrollment" seeds a
      // sensible paid-enrollment form + enables payment by default.
      if (isNew && newType === "enrollment") {
        next.payment_required = true;
        next.form_config = enrollmentFormConfig();
      }
      if (isNew && newType === "event") {
        next.payment_required = false;
      }
      return next;
    });
  };

  const handleSave = async () => {
    try {
      // Save date as-is without timezone compensation
      let dataToSave = { ...formData };

      // Derive a representative `date` (kept required + used for sorting/emails)
      // from the schedule so periodic activities still order by their start.
      const sc = dataToSave.schedule_config || { type: "single" };
      if (sc.type === "range" || sc.type === "weekly") {
        const time = sc.time || "09:00";
        const base =
          sc.startDate ||
          (dataToSave.date ? dataToSave.date.split("T")[0] : "") ||
          new Date().toISOString().split("T")[0];
        dataToSave.date = `${base}T${time}`;
      }

      // Formules zonder naam zijn half ingevulde rijen; die slaan we niet op.
      dataToSave.niveaus = (dataToSave.niveaus || [])
        .filter((n) => String(n.label || "").trim() !== "")
        .map((n) => ({ id: n.id, label: String(n.label).trim() }));

      const niveauIds = new Set(dataToSave.niveaus.map((n) => n.id));
      dataToSave.formules = (dataToSave.formules || [])
        .filter((f) => String(f.label || "").trim() !== "")
        .map((f) => ({
          id: f.id,
          label: String(f.label).trim(),
          // Verwijst de formule naar een niveau dat intussen weg is, dan laten
          // we de koppeling vallen in plaats van een dood id te bewaren.
          niveau_id: niveauIds.has(f.niveau_id) ? f.niveau_id : null,
          // Half ingevulde lesmomenten (geen dag of geen beginuur) laten we
          // vallen — die kunnen we op de site toch niet tonen.
          momenten: (f.momenten || [])
            .filter((m) => m.dag && m.time)
            .map((m) => ({
              id: m.id,
              dag: Number(m.dag),
              time: m.time,
              timeEnd: m.timeEnd || null,
            })),
          price_member: f.price_member === "" ? null : Number(f.price_member),
          price_non_member:
            f.price_non_member === "" ? null : Number(f.price_non_member),
        }));

      if (isNew) {
        await createEvent(dataToSave);
      } else {
        await updateEvent(eventId, dataToSave);
      }
      navigate("/dashboard/events");
    } catch (error) {
      alert("Error saving event");
      console.error(error);
    }
  };

  const handleEmailChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      email_config: { ...prev.email_config, [key]: value },
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert("Bestand is te groot (max 5MB)");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({ ...prev, image: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const addField = () => {
    setFormData((prev) => ({
      ...prev,
      form_config: {
        ...prev.form_config,
        fields: [
          ...(prev.form_config.fields || []),
          {
            _uid: Math.random().toString(36).substr(2, 9),
            id: `field_${Date.now()}`,
            label: "Nieuw veld",
            type: "text",
            required: false,
            options: "",
          },
        ],
      },
    }));
  };

  const removeField = (index) => {
    setFormData((prev) => {
      const newFields = [...prev.form_config.fields];
      newFields.splice(index, 1);
      return {
        ...prev,
        form_config: { ...prev.form_config, fields: newFields },
      };
    });
  };

  const updateField = (index, key, value) => {
    setFormData((prev) => {
      const newFields = [...prev.form_config.fields];
      newFields[index] = { ...newFields[index], [key]: value };
      return {
        ...prev,
        form_config: { ...prev.form_config, fields: newFields },
      };
    });
  };

  const handleReorder = (newFields) => {
    setFormData((prev) => ({
      ...prev,
      form_config: { ...prev.form_config, fields: newFields },
    }));
  };

  const getFieldIcon = (type) => {
    switch (type) {
      case "email":
        return <Mail className="h-4 w-4" />;
      case "number":
        return <Hash className="h-4 w-4" />;
      case "textarea":
        return <AlignLeft className="h-4 w-4" />;
      case "select":
      case "multiselect":
        return <List className="h-4 w-4" />;
      case "checkbox":
        return <CheckSquare className="h-4 w-4" />;
      default:
        return <Type className="h-4 w-4" />;
    }
  };

  if (loading)
    return (
      <div
        className={`flex items-center justify-center h-64 ${isDark ? "text-white" : "text-gray-900"}`}
      >
        <div
          className={`animate-spin rounded-full h-6 w-6 border-2 ${isDark ? "border-white/20 border-t-white" : "border-gray-200 border-t-gray-900"}`}
        />
      </div>
    );

  const tabs = [
    { id: "general", label: "Algemeen", icon: Info },
    { id: "payment", label: "Betaling", icon: CreditCard },
    { id: "form", label: "Formulier", icon: List },
    { id: "email", label: "Emails", icon: Mail },
  ];

  return (
    <div className="space-y-5 pb-20 max-w-6xl mx-auto">
      {/* Header */}
      <div
        className={`flex justify-between items-center p-5 rounded-xl border ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/dashboard/events")}
            className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-400 hover:text-white hover:bg-white/[0.06]" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"}`}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h2
                className={`text-[15px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                {isNew
                  ? isEnrollment
                    ? "Nieuwe inschrijving"
                    : "Nieuw evenement"
                  : `${typeNounCap} bewerken`}
              </h2>
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                  isEnrollment
                    ? isDark
                      ? "bg-blue-500/15 text-blue-300"
                      : "bg-blue-50 text-blue-700"
                    : isDark
                      ? "bg-white/[0.06] text-gray-400"
                      : "bg-gray-100 text-gray-600"
                }`}
              >
                {isEnrollment ? (
                  <GraduationCap className="h-3 w-3" />
                ) : (
                  <Ticket className="h-3 w-3" />
                )}
                {isEnrollment ? "Inschrijving" : "Evenement"}
              </span>
            </div>
            <p
              className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
            >
              Beheer de details en instellingen van je {typeNoun}.
            </p>
          </div>
        </div>
        <button
          onClick={handleSave}
          className={`h-8 px-4 rounded-lg text-[13px] font-medium transition-colors flex items-center gap-2 ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
        >
          <Save className="h-3.5 w-3.5" /> Opslaan
        </button>
      </div>

      {/* Tabs */}
      <div
        className={`flex gap-1 p-1 rounded-xl w-fit ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-gray-100 border border-gray-200/80"}`}
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-1.5 rounded-lg text-[13px] font-medium transition-all inline-flex items-center gap-1.5 ${
                activeTab === tab.id
                  ? isDark
                    ? "bg-white text-black"
                    : "bg-white text-gray-900 shadow-sm"
                  : isDark
                    ? "text-gray-500 hover:text-white"
                    : "text-gray-500 hover:text-gray-900"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content: General */}
      {activeTab === "general" && (
        <div className="space-y-5 mt-1">
          {/* Type chooser */}
          <div
            className={`rounded-xl border p-5 ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Ticket
                className={`h-4 w-4 ${isDark ? "text-blue-400" : "text-blue-600"}`}
              />
              <h3
                className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                Type
              </h3>
            </div>
            <p
              className={`text-[12px] mb-4 ${isDark ? "text-gray-500" : "text-gray-500"}`}
            >
              {isNew
                ? "Kies het type. Een inschrijving (les/stage) kan een betaling vereisen."
                : "Het type van deze activiteit."}
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                {
                  value: "event",
                  label: "Evenement",
                  desc: "Gratis inschrijving voor een evenement of activiteit.",
                  icon: Ticket,
                },
                {
                  value: "enrollment",
                  label: "Inschrijving",
                  desc: "Les, cursus of stage — optioneel met online betaling.",
                  icon: GraduationCap,
                },
              ].map((opt) => {
                const Icon = opt.icon;
                const active = (formData.type || "event") === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={!isNew}
                    onClick={() => handleTypeChange(opt.value)}
                    className={`text-left rounded-xl border p-4 transition-colors ${
                      active
                        ? isDark
                          ? "border-blue-500/50 bg-blue-500/10"
                          : "border-blue-500/40 bg-blue-50"
                        : isDark
                          ? "border-white/[0.08] hover:border-white/[0.16]"
                          : "border-gray-200 hover:border-gray-300"
                    } ${!isNew ? "opacity-70 cursor-not-allowed" : ""}`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon
                        className={`h-4 w-4 ${active ? (isDark ? "text-blue-300" : "text-blue-600") : isDark ? "text-gray-500" : "text-gray-400"}`}
                      />
                      <span
                        className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        {opt.label}
                      </span>
                    </div>
                    <p
                      className={`text-[11px] mt-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                    >
                      {opt.desc}
                    </p>
                  </button>
                );
              })}
            </div>
            {formData.type === "enrollment" && (
              <div className="mt-4 space-y-1.5">
                <label
                  className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  Categorie (website)
                </label>
                <select
                  name="category"
                  value={formData.category || ""}
                  onChange={handleChange}
                  className={`w-full max-w-xs h-9 px-3 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white focus:ring-white/20" : "bg-gray-50 border-gray-200 text-gray-900 focus:ring-gray-300"}`}
                >
                  {ENROLLMENT_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <p
                  className={`text-[11px] flex items-center gap-1.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                >
                  <Info className="h-3 w-3 flex-shrink-0" />
                  Bepaalt onder welke rubriek deze inschrijving op de website
                  verschijnt. Op "Automatisch" wordt er gegroepeerd op basis van
                  de naam.
                </p>

                <div className="pt-4 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-0.5">
                      <label
                        className={`text-[11px] font-medium uppercase tracking-wider block ${isDark ? "text-gray-500" : "text-gray-500"}`}
                      >
                        Niveaus
                      </label>
                      <p
                        className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                      >
                        De groepen waaruit de deelnemer kiest in het formulier.
                        Leeg = er wordt geen niveau gevraagd.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={addNiveau}
                      className={`h-8 px-3 shrink-0 rounded-lg text-[12px] font-medium transition-colors flex items-center gap-1.5 ${isDark ? "bg-white/[0.06] text-white hover:bg-white/[0.1]" : "bg-white border border-gray-200 text-gray-900 hover:bg-gray-100"}`}
                    >
                      <Plus className="h-3.5 w-3.5" /> Niveau
                    </button>
                  </div>
                  {(formData.niveaus || []).map((n) => (
                    <div key={n.id} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={n.label}
                        onChange={(e) => updateNiveau(n.id, e.target.value)}
                        placeholder="Kids Tennis (5 – 12 jaar)"
                        className={`flex-1 max-w-md h-9 px-3 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:ring-white/20" : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-gray-300"}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeNiveau(n.id)}
                        className={`h-9 w-9 shrink-0 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-red-400 hover:bg-white/[0.06]" : "text-gray-400 hover:text-red-600 hover:bg-gray-100"}`}
                        title="Niveau verwijderen"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  {(formData.niveaus || []).length > 0 && (
                    <p
                      className={`text-[11px] flex items-center gap-1.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                    >
                      <Info className="h-3 w-3 flex-shrink-0" />
                      Bij Betalingen kan je elke formule aan een niveau hangen —
                      de website toont dan enkel de formules van het gekozen
                      niveau.
                    </p>
                  )}
                </div>

              </div>
            )}
            {!isNew && (
              <p
                className={`text-[11px] mt-3 flex items-center gap-1 ${isDark ? "text-gray-600" : "text-gray-400"}`}
              >
                <Info className="h-3 w-3" />
                Het type kan na aanmaak niet meer gewijzigd worden.
              </p>
            )}
          </div>

          <div
            className={`rounded-xl border p-5 space-y-5 ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <CalendarIcon
                className={`h-4 w-4 ${isDark ? "text-blue-400" : "text-blue-600"}`}
              />
              <h3
                className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                Naam &amp; planning
              </h3>
            </div>
            <p
              className={`text-[12px] -mt-4 ${isDark ? "text-gray-500" : "text-gray-500"}`}
            >
              Hoe heet deze {typeNoun} en wanneer vindt ze plaats?
            </p>

            <div className="space-y-1.5">
              <label
                className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                Naam {formData.type === "enrollment" ? "Inschrijving" : "Evenement"}
              </label>
              <input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={
                  formData.type === "enrollment"
                    ? "Bv. Lessen Jeugd 2026"
                    : "Bv. Productvoorstelling 2026"
                }
                required
                className={`w-full h-9 px-3 rounded-lg border text-[13px] font-medium transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:ring-white/20" : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-gray-300"}`}
              />
            </div>

            {isEnrollment && (
              <>
                <div className="space-y-1.5">
                  <label
                    className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Lesvorm
                  </label>
                  <select
                    name="lesvorm"
                    value={formData.lesvorm || ""}
                    onChange={handleChange}
                    className={`w-full max-w-xs h-9 px-3 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white focus:ring-white/20" : "bg-gray-50 border-gray-200 text-gray-900 focus:ring-gray-300"}`}
                  >
                    {LESVORMEN.map((l) => (
                      <option key={l.value} value={l.value}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                  <p
                    className={`text-[11px] flex items-center gap-1.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                  >
                    <Info className="h-3 w-3 flex-shrink-0" />
                    Bij "Deelnemer kiest zelf" blijft dit één inschrijving en
                    kiest de deelnemer in het formulier; zijn keuze staat bij de
                    inschrijving.
                  </p>
                </div>

                <div className="grid gap-3 sm:grid-cols-2 max-w-xl">
                  <div className="space-y-1.5">
                    <label
                      className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                    >
                      Aantal lessen
                    </label>
                    <input
                      type="number"
                      min="1"
                      name="aantal_lessen"
                      value={formData.aantal_lessen || ""}
                      onChange={handleChange}
                      placeholder="25"
                      className={`w-full h-9 px-3 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white focus:ring-white/20" : "bg-gray-50 border-gray-200 text-gray-900 focus:ring-gray-300"}`}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label
                      className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                    >
                      Noot bij de periode
                    </label>
                    <input
                      type="text"
                      name="periode_info"
                      value={formData.periode_info || ""}
                      onChange={handleChange}
                      placeholder="excl. schoolvakanties"
                      className={`w-full h-9 px-3 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white focus:ring-white/20" : "bg-gray-50 border-gray-200 text-gray-900 focus:ring-gray-300"}`}
                    />
                  </div>
                </div>
                <p
                  className={`text-[11px] -mt-2 flex items-center gap-1.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                >
                  <Info className="h-3 w-3 flex-shrink-0" />
                  Vul het aantal lessen in — anders telt de website de weken
                  tussen begin- en einddatum, schoolvakanties inbegrepen. Laat
                  je de noot leeg, dan toont de website "excl. schoolvakanties";
                  een streepje (-) toont niets.
                </p>
              </>
            )}

            {/* Planning — eenmalig, periode of wekelijks terugkerend */}
            <div className="space-y-3">
              <label
                className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                Planning
              </label>
              <div
                className={`flex gap-1 p-1 rounded-lg w-fit ${isDark ? "bg-white/[0.04] border border-white/[0.06]" : "bg-gray-100 border border-gray-200"}`}
              >
                {SCHEDULE_TYPES.map((st) => {
                  const active =
                    (formData.schedule_config?.type || "single") === st.value;
                  return (
                    <button
                      key={st.value}
                      type="button"
                      onClick={() => setSchedule({ type: st.value })}
                      title={st.desc}
                      className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-all ${
                        active
                          ? isDark
                            ? "bg-white text-black"
                            : "bg-white text-gray-900 shadow-sm"
                          : isDark
                            ? "text-gray-400 hover:text-white"
                            : "text-gray-500 hover:text-gray-900"
                      }`}
                    >
                      {st.label}
                    </button>
                  );
                })}
              </div>

              {/* Eenmalig */}
              {(formData.schedule_config?.type || "single") === "single" && (
                <div className="flex gap-2 max-w-md">
                  <DatePicker
                    selected={formData.date ? new Date(formData.date) : null}
                    onChange={(date) => {
                      if (!date) {
                        setFormData((prev) => ({ ...prev, date: "" }));
                        return;
                      }
                      const datePart = format(date, "yyyy-MM-dd");
                      const currentTime = formData.date
                        ? formData.date.split("T")[1]
                        : "09:00";
                      setFormData((prev) => ({
                        ...prev,
                        date: `${datePart}T${currentTime}`,
                      }));
                    }}
                    dateFormat="dd/MM/yyyy"
                    className={`w-full h-9 px-3 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:ring-white/20" : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-gray-300"}`}
                    wrapperClassName="flex-1 w-full"
                    placeholderText="dd/mm/yyyy"
                  />
                  <select
                    value={formData.date ? formData.date.split("T")[1] : "09:00"}
                    onChange={(e) => {
                      const newTime = e.target.value;
                      const currentDate = formData.date
                        ? formData.date.split("T")[0]
                        : new Date().toISOString().split("T")[0];
                      setFormData((prev) => ({
                        ...prev,
                        date: `${currentDate}T${newTime}`,
                      }));
                    }}
                    className={`w-28 h-9 px-2 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white focus:ring-white/20" : "bg-gray-50 border-gray-200 text-gray-900 focus:ring-gray-300"}`}
                  >
                    {timeOptions.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Periode (van – tot) */}
              {formData.schedule_config?.type === "range" && (
                <div className="space-y-3">
                  <div className="grid sm:grid-cols-2 gap-3 max-w-lg">
                    <div className="space-y-1">
                      <label
                        className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                      >
                        Startdatum
                      </label>
                      <input
                        type="date"
                        value={formData.schedule_config?.startDate || ""}
                        onChange={(e) =>
                          setSchedule({ startDate: e.target.value })
                        }
                        className={`w-full h-9 px-3 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white focus:ring-white/20 [color-scheme:dark]" : "bg-gray-50 border-gray-200 text-gray-900 focus:ring-gray-300"}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <label
                        className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                      >
                        Einddatum
                      </label>
                      <input
                        type="date"
                        value={formData.schedule_config?.endDate || ""}
                        min={formData.schedule_config?.startDate || undefined}
                        onChange={(e) =>
                          setSchedule({ endDate: e.target.value })
                        }
                        className={`w-full h-9 px-3 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white focus:ring-white/20 [color-scheme:dark]" : "bg-gray-50 border-gray-200 text-gray-900 focus:ring-gray-300"}`}
                      />
                    </div>
                  </div>
                  {renderTimeRange()}
                </div>
              )}

              {/* Wekelijks terugkerend */}
              {formData.schedule_config?.type === "weekly" && (
                <div className="space-y-4">
                  {/* Bij "per formule" liggen de dagen al vast in de
                      lesmomenten van elke formule — dan zou deze keuze een
                      tweede, tegenstrijdige waarheid zijn. */}
                  <div
                    className={`space-y-1.5 ${perFormule ? "hidden" : ""}`}
                  >
                    <span
                      className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                    >
                      Op welke dagen?
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {WEEKDAYS.map((d) => {
                        const on = (
                          formData.schedule_config?.weekdays || []
                        ).includes(d.n);
                        return (
                          <button
                            key={d.n}
                            type="button"
                            onClick={() => toggleWeekday(d.n)}
                            title={d.label}
                            className={`h-9 w-11 rounded-lg border text-[12px] font-medium transition-colors ${
                              on
                                ? isDark
                                  ? "border-blue-500/50 bg-blue-500/15 text-white"
                                  : "border-blue-500/40 bg-blue-50 text-blue-700"
                                : isDark
                                  ? "border-white/[0.08] text-gray-400 hover:border-white/[0.16]"
                                  : "border-gray-200 text-gray-600 hover:border-gray-300"
                            }`}
                          >
                            {d.short}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {renderTimeRange()}
                  <div className="grid sm:grid-cols-2 gap-3 max-w-lg">
                    <div className="space-y-1">
                      <label
                        className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                      >
                        Vanaf (optioneel)
                      </label>
                      <input
                        type="date"
                        value={formData.schedule_config?.startDate || ""}
                        onChange={(e) =>
                          setSchedule({ startDate: e.target.value })
                        }
                        className={`w-full h-9 px-3 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white focus:ring-white/20 [color-scheme:dark]" : "bg-gray-50 border-gray-200 text-gray-900 focus:ring-gray-300"}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <label
                        className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                      >
                        Tot en met (optioneel)
                      </label>
                      <input
                        type="date"
                        value={formData.schedule_config?.endDate || ""}
                        min={formData.schedule_config?.startDate || undefined}
                        onChange={(e) =>
                          setSchedule({ endDate: e.target.value })
                        }
                        className={`w-full h-9 px-3 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white focus:ring-white/20 [color-scheme:dark]" : "bg-gray-50 border-gray-200 text-gray-900 focus:ring-gray-300"}`}
                      />
                    </div>
                  </div>
                </div>
              )}

              <p
                className={`text-[11px] flex items-center gap-1.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
              >
                <Info className="h-3 w-3 flex-shrink-0" />
                {scheduleSummary()}
              </p>
            </div>

            {isEnrollment && (
              <div className="space-y-1.5">
                <label
                  className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  Indeling in groepen
                </label>
                <textarea
                  name="groepsindeling"
                  value={formData.groepsindeling || ""}
                  onChange={handleChange}
                  rows={2}
                  placeholder="vrijdag 11/09/2026 (16u tot 18u) en zaterdag 12/09/2026 (10u tot 12u) in Sharp Recrean"
                  className={`w-full px-3 py-2 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 resize-y ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white focus:ring-white/20" : "bg-gray-50 border-gray-200 text-gray-900 focus:ring-gray-300"}`}
                />
                <p
                  className={`text-[11px] flex items-center gap-1.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                >
                  <Info className="h-3 w-3 flex-shrink-0" />
                  Wanneer en waar de groepen ingedeeld worden. Dit vervangt op de
                  laatste stap van het inschrijfformulier de boodschap dat er nog
                  telefonisch contact volgt.
                </p>
              </div>
            )}
          </div>

          {/* Formules: de varianten binnen deze inschrijving, elk met eigen
              lesmomenten en prijs. Staat hier en niet bij Betalingen, omdat een
              formule vooral een lesmoment is — de prijs hangt eraan vast. */}
          {isEnrollment && (
            <div
              className={`rounded-xl border p-5 space-y-4 ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <List
                      className={`h-4 w-4 ${isDark ? "text-blue-400" : "text-blue-600"}`}
                    />
                    <h3
                      className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                    >
                      Formules
                    </h3>
                  </div>
                  <p
                    className={`text-[12px] mt-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    De varianten binnen deze ene inschrijving — bv. Kids Tennis
                    van 1 uur naast 2 uur. Elke formule heeft haar eigen
                    lesmomenten en prijs; de deelnemer kiest er één.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addFormule}
                  className={`h-8 px-3 shrink-0 rounded-lg text-[12px] font-medium transition-colors flex items-center gap-1.5 ${isDark ? "bg-white/[0.06] text-white hover:bg-white/[0.1]" : "bg-white border border-gray-200 text-gray-900 hover:bg-gray-100"}`}
                >
                  <Plus className="h-3.5 w-3.5" /> Formule
                </button>
              </div>

              {(formData.formules || []).length === 0 && (
                <p
                  className={`text-[11px] flex items-center gap-1.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                >
                  <Info className="h-3 w-3 flex-shrink-0" />
                  Geen formules — dan gelden het uur uit de planning en de
                  enkele prijs bij Betalingen.
                </p>
              )}

              {(formData.formules || []).map((f) => (
                <div
                  key={f.id}
                  className={`rounded-lg border p-4 space-y-3 ${isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-gray-50 border-gray-200/80"}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                    <div className="flex-1 space-y-1.5">
                      <label
                        className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                      >
                        Naam
                      </label>
                      <input
                        type="text"
                        value={f.label}
                        onChange={(e) =>
                          updateFormule(f.id, { label: e.target.value })
                        }
                        placeholder="Kids Tennis · 1 uur"
                        className={`w-full h-9 px-3 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:ring-white/20" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-gray-300"}`}
                      />
                    </div>
                    {(formData.niveaus || []).length > 0 && (
                      <div className="w-full sm:w-44 space-y-1.5">
                        <label
                          className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                        >
                          Niveau
                        </label>
                        <select
                          value={f.niveau_id || ""}
                          onChange={(e) =>
                            updateFormule(f.id, { niveau_id: e.target.value })
                          }
                          className={`w-full h-9 px-3 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white focus:ring-white/20" : "bg-white border-gray-200 text-gray-900 focus:ring-gray-300"}`}
                        >
                          <option value="">Alle niveaus</option>
                          {(formData.niveaus || [])
                            .filter((n) => n.label.trim() !== "")
                            .map((n) => (
                              <option key={n.id} value={n.id}>
                                {n.label}
                              </option>
                            ))}
                        </select>
                      </div>
                    )}
                    <div className="w-full sm:w-28 space-y-1.5">
                      <label
                        className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                      >
                        Leden (€)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={f.price_member}
                        onChange={(e) =>
                          updateFormule(f.id, { price_member: e.target.value })
                        }
                        placeholder="325"
                        className={`w-full h-9 px-3 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:ring-white/20" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-gray-300"}`}
                      />
                    </div>
                    <div className="w-full sm:w-32 space-y-1.5">
                      <label
                        className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                      >
                        Niet-leden (€)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={f.price_non_member}
                        onChange={(e) =>
                          updateFormule(f.id, {
                            price_non_member: e.target.value,
                          })
                        }
                        placeholder="375"
                        className={`w-full h-9 px-3 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:ring-white/20" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-gray-300"}`}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFormule(f.id)}
                      className={`h-9 w-9 shrink-0 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-red-400 hover:bg-white/[0.06]" : "text-gray-400 hover:text-red-600 hover:bg-gray-100"}`}
                      title="Formule verwijderen"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  {/* Lesmomenten — enkel zinvol wanneer de uren per formule
                      verschillen; anders geldt het uur uit de planning. */}
                  {perFormule && (
                    <div
                      className={`pt-3 border-t space-y-2 ${isDark ? "border-white/[0.06]" : "border-gray-200"}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <label
                          className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                        >
                          Lesmomenten
                        </label>
                        <button
                          type="button"
                          onClick={() => addMoment(f.id)}
                          className={`h-7 px-2.5 rounded-lg text-[11px] font-medium transition-colors flex items-center gap-1 ${isDark ? "bg-white/[0.06] text-white hover:bg-white/[0.1]" : "bg-white border border-gray-200 text-gray-900 hover:bg-gray-100"}`}
                        >
                          <Plus className="h-3 w-3" /> Moment
                        </button>
                      </div>
                      {(f.momenten || []).length === 0 && (
                        <p
                          className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                        >
                          Nog geen lesmoment — voeg er één toe per dag dat deze
                          formule doorgaat.
                        </p>
                      )}
                      {(f.momenten || []).map((m) => (
                        <div
                          key={m.id}
                          className="flex flex-wrap items-end gap-2"
                        >
                          <div className="w-36 space-y-1">
                            <label
                              className={`text-[10px] uppercase tracking-wider ${isDark ? "text-gray-600" : "text-gray-400"}`}
                            >
                              Dag
                            </label>
                            <select
                              value={m.dag || ""}
                              onChange={(e) =>
                                updateMoment(f.id, m.id, {
                                  dag: e.target.value,
                                })
                              }
                              className={`w-full h-9 px-2 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white focus:ring-white/20" : "bg-white border-gray-200 text-gray-900 focus:ring-gray-300"}`}
                            >
                              <option value="">— Kies —</option>
                              {WEEKDAGEN.map((d) => (
                                <option key={d.value} value={d.value}>
                                  {d.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          {renderVanTot(m.time, m.timeEnd, (patch) =>
                            updateMoment(f.id, m.id, patch),
                          )}
                          <button
                            type="button"
                            onClick={() => removeMoment(f.id, m.id)}
                            className={`h-9 w-9 shrink-0 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-red-400 hover:bg-white/[0.06]" : "text-gray-400 hover:text-red-600 hover:bg-gray-100"}`}
                            title="Lesmoment verwijderen"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div
            className={`rounded-xl border p-5 space-y-5 ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <MapPin
                className={`h-4 w-4 ${isDark ? "text-blue-400" : "text-blue-600"}`}
              />
              <h3
                className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                Locatie &amp; omschrijving
              </h3>
            </div>
            <p
              className={`text-[12px] -mt-4 ${isDark ? "text-gray-500" : "text-gray-500"}`}
            >
              Waar het plaatsvindt, een afbeelding en de details.
            </p>

            <div className="space-y-1.5">
              <label
                className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                Locatie
              </label>
              <div className="relative">
                <MapPin
                  className={`absolute left-3 top-2.5 h-3.5 w-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                />
                <input
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="Bv. Ter Duinenlaan 34a, Koksijde"
                  className={`w-full h-9 pl-9 pr-3 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:ring-white/20" : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-gray-300"}`}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label
                className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                Afbeelding
              </label>
              <div className="flex gap-4 items-start">
                {(formData.imageUrl ||
                  (formData.image && formData.image.startsWith("data:"))) && (
                  <div className="relative w-28 h-28 rounded-lg overflow-hidden border shrink-0">
                    <img
                      src={
                        formData.image && formData.image.startsWith("data:")
                          ? formData.image
                          : formData.imageUrl
                      }
                      alt="Event"
                      className="w-full h-full object-cover"
                    />
                    <button
                      className="absolute top-1 right-1 h-6 w-6 rounded-md flex items-center justify-center bg-red-500/90 text-white hover:bg-red-600 transition-colors"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          image: "",
                          imageUrl: "",
                        }))
                      }
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                )}
                {!(
                  formData.imageUrl ||
                  (formData.image && formData.image.startsWith("data:"))
                ) && (
                  <div
                    className={`w-28 h-28 rounded-lg flex items-center justify-center border-2 border-dashed shrink-0 ${isDark ? "bg-white/[0.02] border-white/[0.08]" : "bg-gray-50 border-gray-300"}`}
                  >
                    <ImageIcon
                      className={`h-7 w-7 ${isDark ? "text-gray-700" : "text-gray-300"}`}
                    />
                  </div>
                )}
                <div className="space-y-1.5 flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className={`w-full text-[13px] file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-[12px] file:font-medium cursor-pointer ${isDark ? "text-gray-400 file:bg-white/[0.06] file:text-gray-300" : "text-gray-600 file:bg-gray-100 file:text-gray-700"}`}
                  />
                  <p
                    className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                  >
                    Upload een afbeelding voor je {typeNoun} (JPG, PNG). Max 5MB.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <label
                className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                Details & Programma
              </label>
              <textarea
                name="details"
                value={formData.details}
                onChange={handleChange}
                rows={8}
                placeholder="Beschrijf het programma, wat mensen moeten meenemen, etc."
                style={{ tabSize: 4 }}
                className={`w-full px-3 py-2 rounded-lg border text-[13px] font-mono whitespace-pre transition-colors outline-none focus:ring-1 resize-y ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:ring-white/20" : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-gray-300"}`}
              />
            </div>
          </div>

          <div
            className={`rounded-xl border p-5 space-y-5 ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Eye
                className={`h-4 w-4 ${isDark ? "text-blue-400" : "text-blue-600"}`}
              />
              <h3
                className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                Inschrijvingen &amp; zichtbaarheid
              </h3>
            </div>
            <p
              className={`text-[12px] -mt-4 ${isDark ? "text-gray-500" : "text-gray-500"}`}
            >
              Capaciteit, sluitingsmoment en of dit publiek op de website staat.
            </p>

            <div className="space-y-1.5">
              <label
                className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                Maximum Capaciteit
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  name="max_capacity"
                  value={formData.max_capacity || ""}
                  onChange={handleChange}
                  placeholder="Onbeperkt"
                  className={`max-w-[180px] h-9 px-3 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:ring-white/20" : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-gray-300"}`}
                />
                <span
                  className={`text-[12px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                >
                  Laat leeg voor onbeperkt aantal inschrijvingen.
                </span>
              </div>
            </div>

            <div className="space-y-1.5">
              <label
                className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                Inschrijvingen sluiten op
              </label>
              <div className="flex items-center gap-3 flex-wrap">
                <input
                  type="datetime-local"
                  name="registration_deadline"
                  value={formData.registration_deadline || ""}
                  onChange={handleChange}
                  className={`max-w-[240px] h-9 px-3 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white focus:ring-white/20 [color-scheme:dark]" : "bg-gray-50 border-gray-200 text-gray-900 focus:ring-gray-300"}`}
                />
                {formData.registration_deadline && (
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        registration_deadline: "",
                      }))
                    }
                    className={`text-[12px] transition-colors ${isDark ? "text-gray-500 hover:text-red-400" : "text-gray-400 hover:text-red-500"}`}
                  >
                    Wissen
                  </button>
                )}
                <span
                  className={`text-[12px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                >
                  Na dit moment kan niemand nog inschrijven. Leeg = altijd open.
                </span>
              </div>
            </div>

            <div
              className={`flex items-center justify-between space-x-2 p-4 rounded-lg border ${isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-gray-50 border-gray-200/80"}`}
            >
              <div className="space-y-0.5">
                <label
                  className={`text-[13px] font-medium flex items-center gap-2 ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  <Lock
                    className={`h-4 w-4 ${formData.registration_closed ? "text-red-500" : isDark ? "text-gray-600" : "text-gray-400"}`}
                  />
                  {formData.registration_closed
                    ? "Inschrijvingen gesloten"
                    : "Inschrijvingen open"}
                </label>
                <p
                  className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  {formData.registration_closed
                    ? "De website toont “Inschrijvingen gesloten” en weigert nieuwe inschrijvingen."
                    : "Zet dit aan om nu te sluiten, ook als er geen sluitingsmoment ingesteld is."}
                </p>
              </div>
              <Switch
                id="registration-closed"
                checked={!!formData.registration_closed}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({
                    ...prev,
                    registration_closed: checked,
                  }))
                }
              />
            </div>

            <div
              className={`space-y-4 pt-4 border-t ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
            >
              <div className="flex flex-col gap-4">
                <div
                  className={`flex items-center justify-between space-x-2 p-4 rounded-lg border ${isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-gray-50 border-gray-200/80"}`}
                >
                  <div className="space-y-0.5">
                    <label
                      className={`text-[13px] font-medium flex items-center gap-2 ${isDark ? "text-white" : "text-gray-900"}`}
                    >
                      {formData.is_private ? (
                        <Lock className="h-4 w-4 text-orange-500" />
                      ) : (
                        <Eye className="h-4 w-4 text-green-500" />
                      )}
                      {formData.is_private ? "Privé" : "Publiek zichtbaar"}
                    </label>
                    <p
                      className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                    >
                      {formData.is_private
                        ? "Verborgen op de website. Schakel in om publiek te maken."
                        : "Zichtbaar voor iedereen op de website."}
                    </p>
                  </div>
                  <Switch
                    id="public-mode"
                    checked={!formData.is_private}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({ ...prev, is_private: !checked }))
                    }
                  />
                </div>

                {!isNew && (
                  <div className="space-y-1.5">
                    <label
                      className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                    >
                      Deelbare Link
                    </label>
                    <div className="flex items-center gap-2">
                      <div
                        className={`flex-1 px-3 py-2 rounded-lg border font-mono text-[12px] truncate ${isDark ? "bg-white/[0.04] border-white/[0.06] text-gray-400" : "bg-gray-50 border-gray-200 text-gray-600"}`}
                      >
                        {shareUrl || "Geen domeinnaam ingesteld voor dit bedrijf"}
                      </div>
                      <button
                        disabled={!shareUrl}
                        onClick={() => {
                          if (!shareUrl) return;
                          navigator.clipboard.writeText(shareUrl);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        }}
                        title="Kopieer link"
                        className={`h-8 w-8 rounded-lg flex items-center justify-center border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                          copied
                            ? "text-green-500 border-green-500/30 bg-green-500/10"
                            : isDark
                              ? "border-white/[0.08] text-gray-500 hover:text-white hover:bg-white/[0.06]"
                              : "border-gray-200 text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                        }`}
                      >
                        {copied ? (
                          <Check className="h-3.5 w-3.5" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                    {copied && (
                      <p className="text-[11px] text-green-500 font-medium">
                        Link gekopieerd!
                      </p>
                    )}
                    {!shareUrl && (
                      <p className="text-[11px] text-orange-500 flex items-center gap-1">
                        <Info className="h-3 w-3" />
                        Stel een domeinnaam in bij dit bedrijf om een deelbare
                        link te genereren.
                      </p>
                    )}
                    {shareUrl && formData.is_private && (
                      <p className="text-[11px] text-orange-500 flex items-center gap-1">
                        <Info className="h-3 w-3" />
                        Gebruik deze link om mensen uit te nodigen.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div
            className={`rounded-xl border p-5 space-y-4 ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
          >
            <div className="flex items-center gap-2">
              <Bell
                className={`h-4 w-4 ${isDark ? "text-orange-400" : "text-orange-500"}`}
              />
              <h3
                className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                Interne Notificaties
              </h3>
            </div>
            <div className="max-w-md space-y-1.5">
              <label
                className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                Notificatie E-mail
              </label>
              <input
                type="email"
                name="notification_email"
                value={formData.notification_email}
                onChange={handleChange}
                placeholder="info@bedrijf.be"
                className={`w-full h-9 px-3 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:ring-white/20" : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-gray-300"}`}
              />
              <p
                className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
              >
                Ontvang een mailtje bij elke nieuwe inschrijving.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tab Content: Payment */}
      {activeTab === "payment" && (
        <div className="space-y-5 mt-1">
          <div
            className={`rounded-xl border p-5 space-y-5 ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <CreditCard
                className={`h-4 w-4 ${isDark ? "text-emerald-400" : "text-emerald-600"}`}
              />
              <h3
                className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                Betaling
              </h3>
            </div>
            <p
              className={`text-[12px] -mt-4 ${isDark ? "text-gray-500" : "text-gray-500"}`}
            >
              Vereis een online betaling (Mollie) bij inschrijving. De prijs
              wordt server-side bepaald op basis van de lidstatus.
            </p>

            <div
              className={`flex items-center justify-between space-x-2 p-4 rounded-lg border ${isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-gray-50 border-gray-200/80"}`}
            >
              <div className="space-y-0.5">
                <label
                  className={`text-[13px] font-medium flex items-center gap-2 ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  <Euro
                    className={`h-4 w-4 ${formData.payment_required ? "text-emerald-500" : isDark ? "text-gray-600" : "text-gray-400"}`}
                  />
                  Online betaling vereist
                </label>
                <p
                  className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  {formData.payment_required
                    ? "Deelnemers betalen via Mollie. Inschrijving is pas bevestigd na betaling."
                    : "Gratis inschrijving — geen betaling vereist."}
                </p>
              </div>
              <Switch
                id="payment-required"
                checked={!!formData.payment_required}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({
                    ...prev,
                    payment_required: checked,
                    // Een keuze zonder verplichte betaling heeft geen zin.
                    payment_choice_allowed: checked
                      ? prev.payment_choice_allowed
                      : false,
                  }))
                }
              />
            </div>

            {formData.payment_required && (
              <div
                className={`flex items-center justify-between space-x-2 p-4 rounded-lg border ${isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-gray-50 border-gray-200/80"}`}
              >
                <div className="space-y-0.5">
                  <label
                    className={`text-[13px] font-medium flex items-center gap-2 ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    <Clock
                      className={`h-4 w-4 ${formData.payment_choice_allowed ? "text-emerald-500" : isDark ? "text-gray-600" : "text-gray-400"}`}
                    />
                    Laat kiezen: nu of later betalen
                  </label>
                  <p
                    className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    {formData.payment_choice_allowed
                      ? "Deelnemer kiest zelf: direct online (Mollie), of later betalen bij het planningsmoment. Bij \"later\" is de inschrijving meteen bevestigd, zonder online betaling."
                      : "Betaling is verplicht en verloopt altijd meteen via Mollie."}
                  </p>
                </div>
                <Switch
                  id="payment-choice-allowed"
                  checked={!!formData.payment_choice_allowed}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({
                      ...prev,
                      payment_choice_allowed: checked,
                    }))
                  }
                />
              </div>
            )}

            {formData.payment_required &&
              (formData.formules || []).length > 0 && (
                <p
                  className={`text-[11px] flex items-center gap-1.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                >
                  <Info className="h-3 w-3 flex-shrink-0" />
                  Deze inschrijving werkt met formules — hun prijzen staan bij
                  Algemeen, samen met de lesmomenten van elke formule.
                </p>
              )}

            {/* De enkele prijs geldt enkel zonder formules; met formules staan
                de bedragen per formule bij Algemeen. */}
            {formData.payment_required &&
              (formData.formules || []).length === 0 && (
              <div className="grid md:grid-cols-2 gap-5 max-w-2xl">
                <div className="space-y-1.5">
                  <label
                    className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Ledenprijs (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    name="price_member"
                    value={formData.price_member}
                    onChange={handleChange}
                    placeholder="Bv. 130"
                    className={`w-full h-9 px-3 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:ring-white/20" : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-gray-300"}`}
                  />
                </div>
                <div className="space-y-1.5">
                  <label
                    className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Niet-ledenprijs (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    name="price_non_member"
                    value={formData.price_non_member}
                    onChange={handleChange}
                    placeholder="Bv. 150"
                    className={`w-full h-9 px-3 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:ring-white/20" : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-gray-300"}`}
                  />
                </div>
                <div className="md:col-span-2">
                  <p
                    className={`text-[11px] flex items-center gap-1.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                  >
                    <Info className="h-3 w-3 flex-shrink-0" />
                    Zorg dat het formulier een veld bevat waarmee de deelnemer
                    de lidstatus aangeeft. De consumer stuurt dit als{" "}
                    <code
                      className={`px-1 rounded ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
                    >
                      isMember
                    </code>{" "}
                    mee.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab Content: Form Builder */}
      {activeTab === "form" && (
        <div className="grid lg:grid-cols-3 gap-5 mt-1">
          <div className="lg:col-span-2 space-y-4">
            <div className="flex justify-between items-start gap-3 mb-1">
              <div>
                <div className="flex items-center gap-2">
                  <List
                    className={`h-4 w-4 ${isDark ? "text-blue-400" : "text-blue-600"}`}
                  />
                  <h3
                    className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    Inschrijfformulier
                  </h3>
                </div>
                <p
                  className={`text-[12px] mt-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  Welke gegevens vraag je aan de deelnemer? Sleep om te
                  herordenen.
                </p>
              </div>
              <button
                onClick={addField}
                className={`shrink-0 h-8 px-3 rounded-lg text-[12px] font-medium border transition-colors flex items-center gap-1.5 ${isDark ? "border-white/[0.08] text-gray-400 hover:text-white hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
              >
                <Plus className="h-3.5 w-3.5" /> Veld toevoegen
              </button>
            </div>

            <div className="space-y-3">
              <Reorder.Group
                axis="y"
                values={formData.form_config.fields || []}
                onReorder={handleReorder}
                className="space-y-3"
              >
                {(formData.form_config.fields || []).map((field, index) => (
                  <DraggableField
                    key={(field._uid || field.id) + field.type}
                    field={field}
                    index={index}
                    updateField={updateField}
                    removeField={removeField}
                    getFieldIcon={getFieldIcon}
                    isDark={isDark}
                  />
                ))}
              </Reorder.Group>

              {formData.form_config.fields?.length === 0 && (
                <div
                  className={`text-center py-10 border-2 border-dashed rounded-xl ${isDark ? "border-white/[0.08] bg-white/[0.02]" : "border-gray-200 bg-gray-50"}`}
                >
                  <p
                    className={`text-[13px] mb-3 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                  >
                    Nog geen velden toegevoegd
                  </p>
                  <button
                    onClick={addField}
                    className={`h-8 px-3 rounded-lg text-[12px] font-medium border transition-colors flex items-center gap-1.5 mx-auto ${isDark ? "border-white/[0.08] text-gray-400 hover:text-white hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                  >
                    <Plus className="h-3.5 w-3.5" /> Eerste veld toevoegen
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Preview Panel */}
          <div className="hidden lg:block">
            <div
              className={`sticky top-6 rounded-xl border p-5 ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-gray-50 border-gray-200/80"}`}
            >
              <div
                className={`flex items-center gap-2 mb-4 text-[12px] font-medium ${isDark ? "text-gray-500" : "text-gray-400"}`}
              >
                <Eye className="h-3.5 w-3.5" />
                Live Voorbeeld
              </div>
              <div
                className={`p-5 rounded-lg border space-y-4 ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80 shadow-sm"}`}
              >
                <h4
                  className={`font-semibold text-[15px] border-b pb-2 ${isDark ? "text-white border-white/[0.06]" : "text-gray-900 border-gray-100"}`}
                >
                  {formData.name || `${typeNounCap} naam`}
                </h4>
                {formData.date && (
                  <div
                    className={`flex items-center text-[12px] gap-2 mb-4 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    <CalendarIcon className="h-3.5 w-3.5" />
                    {format(new Date(formData.date), "dd/MM/yyyy HH:mm", {
                      locale: nl,
                    })}
                  </div>
                )}
                {formData.form_config.fields?.map((f, i) => (
                  <div key={i} className="space-y-1">
                    <label
                      className={`text-[12px] ${isDark ? "text-gray-400" : "text-gray-600"}`}
                    >
                      {f.label}{" "}
                      {f.required && <span className="text-red-500">*</span>}
                    </label>
                    {f.type === "textarea" ? (
                      <div
                        className={`h-16 w-full rounded-lg border ${isDark ? "bg-white/[0.04] border-white/[0.06]" : "bg-gray-50 border-gray-200"}`}
                      />
                    ) : f.type === "checkbox" ? (
                      <div className="flex items-center gap-2">
                        <Checkbox disabled />
                        <span
                          className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                        >
                          {f.label}
                        </span>
                      </div>
                    ) : f.type === "select" ? (
                      <select
                        disabled
                        className={`h-8 w-full rounded-lg border px-2 text-[12px] opacity-70 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-gray-500" : "bg-gray-50 border-gray-200 text-gray-500"}`}
                      >
                        <option>Selecteer...</option>
                        {f.options &&
                          f.options
                            .split(",")
                            .map((o) => <option key={o}>{o.trim()}</option>)}
                      </select>
                    ) : f.type === "multiselect" ? (
                      <div
                        className={`border rounded-lg p-2 space-y-1 ${isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-gray-50 border-gray-200"}`}
                      >
                        {f.options &&
                          f.options.split(",").map((o, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <Checkbox disabled />
                              <span
                                className={`text-[11px] ${isDark ? "text-gray-500" : "text-gray-600"}`}
                              >
                                {o.trim()}
                              </span>
                            </div>
                          ))}
                        {(!f.options || !f.options.trim()) && (
                          <span
                            className={`text-[11px] italic ${isDark ? "text-gray-600" : "text-gray-400"}`}
                          >
                            Geen opties ingesteld
                          </span>
                        )}
                      </div>
                    ) : (
                      <div
                        className={`h-8 w-full rounded-lg border ${isDark ? "bg-white/[0.04] border-white/[0.06]" : "bg-gray-50 border-gray-200"}`}
                      />
                    )}
                  </div>
                ))}
                <button
                  className={`w-full mt-4 h-9 rounded-lg text-[13px] font-medium opacity-50 cursor-not-allowed ${isDark ? "bg-white text-black" : "bg-gray-900 text-white"}`}
                  disabled
                >
                  Inschrijven
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Content: Email */}
      {activeTab === "email" && (
        <div className="grid lg:grid-cols-2 gap-5 mt-1">
          <div className="space-y-5">
            <div
              className={`rounded-xl border p-5 space-y-4 ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Mail
                  className={`h-4 w-4 ${isDark ? "text-blue-400" : "text-blue-600"}`}
                />
                <h3
                  className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  Bevestigingsmail
                </h3>
              </div>
              <p
                className={`text-[12px] -mt-3 ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                Dit bericht wordt automatisch naar de deelnemer gestuurd na een
                succesvolle inschrijving.
              </p>

              <div className="space-y-1.5">
                <label
                  className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  Onderwerp
                </label>
                <input
                  value={formData.email_config.subject}
                  onChange={(e) => handleEmailChange("subject", e.target.value)}
                  placeholder="Bevestiging inschrijving..."
                  className={`w-full h-9 px-3 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:ring-white/20" : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-gray-300"}`}
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <label
                    className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Berichttekst
                  </label>
                  <span
                    className={`text-[10px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                  >
                    Ondersteunt variabelen & HTML
                  </span>
                </div>
                <textarea
                  className={`w-full min-h-[400px] px-3 py-2 rounded-lg border text-[13px] font-mono leading-relaxed transition-colors outline-none focus:ring-1 resize-y ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:ring-white/20" : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-gray-300"}`}
                  value={formData.email_config.body}
                  onChange={(e) => handleEmailChange("body", e.target.value)}
                  placeholder="<p>Beste [Naam],</p> ..."
                />
                <p
                  className={`text-[10px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                >
                  Tip: Gebruik &lt;strong&gt;<strong>vette tekst</strong>
                  &lt;/strong&gt; of &lt;br&gt; voor nieuwe regels.
                </p>
              </div>
            </div>

            <div
              className={`rounded-xl border p-5 space-y-3 ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
            >
              <h4
                className={`text-[12px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                Beschikbare Variabelen
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {formData.form_config.fields?.map((f, i) => (
                  <button
                    key={i}
                    className={`px-2 py-1 rounded-md text-[11px] font-mono border transition-colors cursor-pointer ${isDark ? "border-white/[0.08] text-gray-400 hover:text-white hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                    onClick={() =>
                      handleEmailChange(
                        "body",
                        formData.email_config.body + ` [${f.label}]`,
                      )
                    }
                  >
                    [{f.label}]
                  </button>
                ))}
                <span
                  className={`px-2 py-1 rounded-md text-[11px] font-mono ${isDark ? "bg-white/[0.06] text-gray-400" : "bg-gray-100 text-gray-600"}`}
                >
                  [Evenement]
                </span>
                <span
                  className={`px-2 py-1 rounded-md text-[11px] font-mono ${isDark ? "bg-white/[0.06] text-gray-400" : "bg-gray-100 text-gray-600"}`}
                >
                  [Datum]
                </span>
              </div>
              <p
                className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
              >
                Klik op een variabele om deze toe te voegen aan je bericht.
              </p>
            </div>

            <div
              className={`rounded-xl border p-5 space-y-4 ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Bell
                  className={`h-4 w-4 ${isDark ? "text-orange-400" : "text-orange-500"}`}
                />
                <h3
                  className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  Herinneringsmail
                </h3>
              </div>
              <div
                className={`flex items-center justify-between space-x-2 p-4 rounded-lg border ${isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-gray-50 border-gray-200/80"}`}
              >
                <div className="space-y-0.5">
                  <label
                    className={`text-[13px] font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    {formData.reminder_enabled
                      ? "Herinnering staat aan"
                      : "Geen herinnering"}
                  </label>
                  <p
                    className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Deelnemers krijgen automatisch een mailtje vlak voor de
                    startdatum.
                  </p>
                </div>
                <Switch
                  id="reminder-enabled"
                  checked={!!formData.reminder_enabled}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({
                      ...prev,
                      reminder_enabled: checked,
                    }))
                  }
                />
              </div>

              {formData.reminder_enabled && (
                <div className="space-y-1.5">
                  <label
                    className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Hoeveel dagen vooraf
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min="1"
                      max="60"
                      name="reminder_days_before"
                      value={formData.reminder_days_before}
                      onChange={handleChange}
                      className={`max-w-[100px] h-9 px-3 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white focus:ring-white/20" : "bg-gray-50 border-gray-200 text-gray-900 focus:ring-gray-300"}`}
                    />
                    <span
                      className={`text-[12px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                    >
                      dagen voor de startdatum, elke ochtend verstuurd.
                    </span>
                  </div>
                  <p
                    className={`text-[11px] flex items-center gap-1.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                  >
                    <Info className="h-3 w-3 flex-shrink-0" />
                    Iedereen krijgt deze mail maximaal één keer. Geannuleerde en
                    onbetaalde inschrijvingen worden overgeslagen.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Email Preview */}
          <div className="hidden lg:block">
            <div
              className={`rounded-xl border p-5 h-full ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-gray-50 border-gray-200/80"}`}
            >
              <p
                className={`text-[12px] font-medium mb-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}
              >
                Voorbeeld Weergave
              </p>
              <div
                className={`rounded-lg border p-8 max-w-lg mx-auto min-h-[500px] ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-white border-gray-200/80 shadow-sm"}`}
              >
                <div
                  className={`border-b pb-4 mb-6 ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
                >
                  <div
                    className={`text-xl font-bold ${isDark ? "text-white" : "text-gray-800"}`}
                  >
                    ZICHTBAAR
                  </div>
                </div>
                <div className="space-y-4">
                  <h2
                    className={`text-[16px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    {formData.email_config.subject || "Onderwerp..."}
                  </h2>
                  <div
                    className={`text-[13px] leading-relaxed whitespace-pre-line ${isDark ? "text-gray-400" : "text-gray-600"}`}
                    dangerouslySetInnerHTML={{
                      __html: (
                        formData.email_config.body || "Typ je bericht hier..."
                      )
                        .replace(
                          /\[(.*?)\]/g,
                          '<span class="bg-yellow-100 text-yellow-800 px-1 rounded font-medium">[$1]</span>',
                        )
                        .replace(
                          /\n/g,
                          formData.email_config.body?.includes("<")
                            ? "\n"
                            : "<br/>",
                        ),
                    }}
                  />
                </div>
                <div
                  className={`mt-8 pt-6 border-t text-[11px] text-center ${isDark ? "border-white/[0.06] text-gray-600" : "border-gray-100 text-gray-400"}`}
                >
                  &copy; {new Date().getFullYear()} Zichtbaar. Alle rechten
                  voorbehouden.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventEditor;

const DraggableField = ({
  field,
  index,
  updateField,
  removeField,
  getFieldIcon,
  isDark,
}) => {
  const controls = useDragControls();

  // Helper to slugify label for ID
  const generateSlug = (label) => {
    return label
      .toString()
      .toLowerCase()
      .trim()
      .replace(/[\s\W-]+/g, "_")
      .replace(/^_|_$/g, "");
  };

  const handleLabelChange = (e) => {
    const newLabel = e.target.value;
    updateField(index, "label", newLabel);

    // Only auto-update ID if it looks like a generated timestamp id or matches the old slug
    const currentSlug = generateSlug(field.label);
    if (field.id.startsWith("field_") || field.id === currentSlug) {
      updateField(index, "id", generateSlug(newLabel) || `field_${Date.now()}`);
    }
  };

  return (
    <Reorder.Item
      value={field}
      id={field._uid || field.id}
      dragListener={false}
      dragControls={controls}
      className="relative"
    >
      <div
        className={`relative group rounded-xl border transition-colors ${isDark ? "bg-white/[0.03] border-white/[0.06] hover:border-white/[0.12]" : "bg-white border-gray-200/80 hover:border-blue-300"}`}
      >
        <div
          className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl transition-colors ${isDark ? "bg-white/[0.06] group-hover:bg-blue-500" : "bg-gray-100 group-hover:bg-blue-500"}`}
        />
        <div className="p-4 pl-5">
          <div className="flex gap-3 items-start">
            <div
              className={`mt-2 cursor-move touch-none ${isDark ? "text-gray-600 hover:text-gray-400" : "text-gray-400 hover:text-gray-600"}`}
              onPointerDown={(e) => controls.start(e)}
            >
              <GripVertical className="h-4 w-4" />
            </div>

            <div className="flex-1 grid gap-3">
              <div className="grid md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label
                    className={`text-[11px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Label
                  </label>
                  <input
                    value={field.label}
                    onChange={handleLabelChange}
                    className={`w-full h-8 px-3 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:ring-white/20" : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-gray-300"}`}
                  />
                  <p
                    className={`text-[10px] flex items-center gap-1 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                  >
                    <code
                      className={`px-1 rounded ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
                    >
                      {field.id}
                    </code>
                    (systeem naam)
                  </p>
                </div>
                <div className="space-y-1">
                  <label
                    className={`text-[11px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Type
                  </label>
                  <div className="relative">
                    <div
                      className={`absolute left-2.5 top-2 pointer-events-none ${isDark ? "text-gray-600" : "text-gray-400"}`}
                    >
                      {getFieldIcon(field.type)}
                    </div>
                    <select
                      className={`w-full h-8 pl-8 pr-3 rounded-lg border text-[13px] transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white focus:ring-white/20" : "bg-gray-50 border-gray-200 text-gray-900 focus:ring-gray-300"}`}
                      value={field.type}
                      onChange={(e) =>
                        updateField(index, "type", e.target.value)
                      }
                    >
                      <option value="text">Tekst</option>
                      <option value="email">Email</option>
                      <option value="number">Nummer</option>
                      <option value="textarea">Lange tekst</option>
                      <option value="select">Keuzelijst</option>
                      <option value="checkbox">Aankruisvak (Ja/Nee)</option>
                      <option value="multiselect">Meerdere opties</option>
                    </select>
                  </div>
                </div>
              </div>

              {["select", "multiselect"].includes(field.type) && (
                <div className="space-y-1">
                  <label
                    className={`text-[11px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Opties (komma gescheiden)
                  </label>
                  <input
                    placeholder="Optie 1, Optie 2, Optie 3"
                    value={field.options || ""}
                    onChange={(e) =>
                      updateField(index, "options", e.target.value)
                    }
                    className={`w-full h-8 px-3 rounded-lg border text-[13px] font-mono transition-colors outline-none focus:ring-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:ring-white/20" : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-gray-300"}`}
                  />
                </div>
              )}

              <div className="flex items-center gap-2">
                <div className="flex items-center space-x-2">
                  <Switch
                    id={`req-${field.id || index}`}
                    checked={field.required}
                    onCheckedChange={(c) => updateField(index, "required", c)}
                  />
                  <label
                    htmlFor={`req-${field.id || index}`}
                    className={`text-[12px] font-normal ${isDark ? "text-gray-400" : "text-gray-600"}`}
                  >
                    Verplicht veld
                  </label>
                </div>
              </div>
            </div>

            <button
              className={`h-7 w-7 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-600 hover:text-red-400 hover:bg-red-500/10" : "text-gray-400 hover:text-red-500 hover:bg-red-50"}`}
              onClick={() => removeField(index)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </Reorder.Item>
  );
};
